/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { useLazyGetAddressTypeQuery } from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { Address, AddressData, ColoniaStructure, PayloadPersonPatch } from "@interfaces/index";
import { InactivityHandler } from "@components/InactivityHandler";
import { ADDRESS_CODES, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { useLazyGetPersonsQuery, usePatchPersonMutation } from "@api/personasApi";
import { UUID } from "crypto";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { CURP, EMAIL } from "@utils/expreciones";
import CustomCountryCode from "@components/CustomContryCode/CustomContryCode";

const InformacionConyugueAval: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [getByCp] = useLazyGetByCpQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const application = useSelector((state: RootState) => state.register.application);
  const personConyugeAval = useSelector((state: RootState) => state.bofu.aval_conyuge);

  const [addresType, setAddresType] = useState<Address | undefined>(undefined);
  const [colonias, setColonias] = useState<{ id: number; name: string }[]>([]);
  const [address, setAddress] = useState<AddressData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [getPersons] = useLazyGetPersonsQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [getAddress] = useLazyGetAddressQuery();
  const [getAddresType] = useLazyGetAddressTypeQuery();

  const handleOnClickRestart = () => {
    if (application.tax_system_type === TAX_SYSTEM_TYPE.PM.code) {
      navigate("/producto/creditosimple/avance_expediente_digital");
    } else {
      navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
    }
  };

  const formik = useFormik({
    initialValues: {
      ocupacion: "",
      codigoPostal: "",
      calle: "",
      noInterior: "",
      noExterior: "",
      estadoId: "",
      ciudadId: "",
      localidadId: "",
      coloniaId: "",
      estado: "",
      ciudad: "",
      localidad: "",
      otro: "",
      curp: "",
      country_code: "+52",
      telefono: "",
      email: "",
    },
    validationSchema: yup.object({
      ocupacion: yup.string().required("Requerido"),
      codigoPostal: yup
        .string()
        .required("Requerido")
        .matches(/^[0-9]{5}$/, "Debe ser un código postal válido"),
      calle: yup.string().required("Requerido"),
      noInterior: yup.string().notRequired(),
      noExterior: yup.string().required(),
      estadoId: yup.string().required("Requerido"),
      ciudadId: yup.string(),
      localidadId: yup.string().required("Requerido"),
      coloniaId: yup.string().required("Requerido"),
      curp: yup.string().required("Campo requerido").matches(CURP, "Formato de CURP no válido"),
      country_code: yup.string().required("Requerido"),
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      telefono: yup
        .string()
        .required("Requerido")
        .matches(/^[0-9]{10}$/, "Debe ser un número de 10 dígitos"),
      otro: yup.string().when("coloniaId", {
        is: "-1",
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!personConyugeAval) {
          throw new Error("No se encontró el conyugue o aval");
        }

        const payload_address = {
          person_id: personConyugeAval.id,
          address_type_id: addresType?.id,
          cp: values.codigoPostal,
          street: values.calle,
          no_ext: values.noExterior,
          no_int: values.noInterior,
          country_id: 1,
          state_id: values.estadoId ? +values.estadoId : null,
          city_id: values.ciudadId ? +values.ciudadId : null,
          locality_id: values.localidadId ? +values.localidadId : null,
          colonia_id: Number(values.coloniaId) === -1 ? null : Number(values.coloniaId),
          other_neighborhood: values.otro,
        };

        if (address?.id) {
          await patchAddress({
            addressId: address.id as UUID,
            data: payload_address,
          }).unwrap();
        } else {
          await postAddress(payload_address).unwrap();
        }

        const payload: PayloadPersonPatch = {
          occupation: values.ocupacion,
          information_person_completed: true,
          personal_information_status: "COMPLETO",
          code_phone_number: values.country_code,
          phone_number: values.telefono,
          email: values.email,
          curp: values.curp,
        };

        await patchPerson({
          personId: personConyugeAval.id,
          body: payload,
        }).unwrap();

        if (application.tax_system_type === TAX_SYSTEM_TYPE.PM.code) {
          navigate("/producto/creditosimple/docs_aval");
        } else {
          navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
        }
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionConyugeAval-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchLocationData = async (codigoPostal: string, coloniaId?: number) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setColonias(colonias);

        formik.setFieldValue("estadoId", states[0]?.id || "");
        formik.setFieldValue("estado", states[0]?.name || "");
        formik.setFieldValue("ciudadId", cities[0]?.id || "");
        formik.setFieldValue("ciudad", cities[0]?.name || "");
        formik.setFieldValue("localidadId", localities[0]?.id || "");
        formik.setFieldValue("localidad", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: ColoniaStructure) => item.id == coloniaId);
          formik.setFieldValue("coloniaId", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("coloniaId", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("estadoId", "");
        formik.setFieldValue("estado", "");
        formik.setFieldValue("ciudadId", "");
        formik.setFieldValue("ciudad", "");
        formik.setFieldValue("localidadId", "");
        formik.setFieldValue("localidad", "");
        formik.setFieldValue("coloniaId", "");
        formik.setFieldValue("colonia", "");
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        if (!personConyugeAval) {
          throw new Error("No se encontró el conyugue o aval");
        }

        setIsLoading(true);

        const [persons, res_address_type, res_address] = await Promise.all([
          getPersons({
            loanApplicationId: application.id,
            id: personConyugeAval.id,
          }).unwrap(),
          getAddresType().unwrap(),
          getAddress({
            personId: personConyugeAval.id,
          }).unwrap(),
        ]);

        const aval_conyuge = persons[0];
        const address_type = res_address_type.find((item: Address) => item.code === ADDRESS_CODES.particular.code);
        const address = res_address.find((item: AddressData) => item.address_type_id === address_type?.id);

        setAddresType(address_type);
        setAddress(address);

        formik.setFieldValue("ocupacion", aval_conyuge.occupation || "");
        formik.setFieldValue("curp", aval_conyuge.curp || "");
        formik.setFieldValue("email", aval_conyuge.email || "");
        formik.setFieldValue("telefono", aval_conyuge.phone_number || "");
        formik.setFieldValue("country_code", aval_conyuge.code_phone_number || "+52");

        if (address) {
          formik.setFieldValue("codigoPostal", address.cp || "");
          formik.setFieldValue("calle", address.street || "");
          formik.setFieldValue("noInterior", address.no_int || "");
          formik.setFieldValue("noExterior", address.no_ext || "");
          formik.setFieldValue("otro", address.other_neighborhood || "");
          await fetchLocationData(address.cp, address.colonia_id || -1);
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionConyugeAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (String(formik.values.coloniaId) !== "-1") {
      formik.setFieldValue("otro", "");
    }
  }, [formik.values.coloniaId]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Información del conyuge
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <FormControl fullWidth>
              <TextField
                id="ocupacion"
                name="ocupacion"
                label="Ocupación"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.ocupacion && Boolean(formik.errors.ocupacion)}
                helperText={formik.touched.ocupacion && formik.errors.ocupacion}
                value={formik.values.ocupacion}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="curp"
                name="curp"
                label="CURP"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={(e) => {
                  const input = e.target;
                  const value = e.target.value.toUpperCase();
                  const start = input.selectionStart;
                  formik.handleChange(e);
                  formik.setFieldValue("curp", value);
                  setTimeout(() => {
                    input.setSelectionRange(start, start);
                  }, 0);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.curp}
                error={formik.touched.curp && Boolean(formik.errors.curp)}
                helperText={formik.touched.curp && formik.errors.curp}
                inputProps={{ maxLength: 18 }}
                disabled={isLoading}
              />
            </FormControl>

            <Grid container spacing={1} mt={1}>
              <Grid item xs={4} sm={4} md={4}>
                <CustomCountryCode
                  label="Código"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                />
              </Grid>

              <Grid item xs={8} sm={8} md={8}>
                <TextField
                  type="text"
                  name="telefono"
                  variant="outlined"
                  label="Teléfono"
                  fullWidth
                  required
                  value={formik.values.telefono}
                  onChange={(event) => {
                    formik.setFieldValue("telefono", event.target.value.replace(/[^0-9]/g, ""));
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.telefono && Boolean(formik.errors.telefono)}
                  helperText={formik.touched.telefono && formik.errors.telefono}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <TextField
                id="email"
                name="email"
                label="Correo electrónico"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const lowerValue = e.target.value.toLowerCase();
                  formik.setFieldValue("email", lowerValue);
                }}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email ? formik.errors.email : ""}
                inputProps={{ maxLength: 100 }}
                disabled={formik.isSubmitting}
              />
            </FormControl>

            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              fontWeight={600}
              mt={2}
            >
              Domicilio:
            </Typography>

            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              fontWeight={400}
              mb={2}
            >
              Deberá ser un domicilio dentro de la República Mexicana
            </Typography>

            <FormControl fullWidth>
              <TextField
                fullWidth
                id="codigoPostal"
                name="codigoPostal"
                label="Código Postal"
                required
                value={formik.values.codigoPostal}
                onChange={(e) => {
                  const cp = e.target.value.replace(/[^0-9]/g, "");
                  formik.setFieldValue("codigoPostal", cp);
                  if (cp.length === 5) {
                    fetchLocationData(cp);
                  }
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.codigoPostal && Boolean(formik.errors.codigoPostal)}
                helperText={formik.touched.codigoPostal ? formik.errors.codigoPostal : ""}
                margin="normal"
                disabled={isLoading || formik.isSubmitting}
                inputProps={{ maxLength: 5 }}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                fullWidth
                id="calle"
                name="calle"
                label="Calle o avenida"
                required
                value={formik.values.calle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.calle && Boolean(formik.errors.calle)}
                helperText={formik.touched.calle ? formik.errors.calle : ""}
                margin="normal"
                disabled={isLoading || formik.isSubmitting}
                inputProps={{ maxLength: 100 }}
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="noExterior"
                    name="noExterior"
                    label="No. Exterior"
                    required
                    value={formik.values.noExterior}
                    onChange={formik.handleChange}
                    error={formik.touched.noExterior && Boolean(formik.errors.noExterior)}
                    helperText={formik.touched.noExterior ? formik.errors.noExterior : ""}
                    margin="normal"
                    disabled={isLoading || formik.isSubmitting}
                    inputProps={{ maxLength: 50 }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    id="noInterior"
                    name="noInterior"
                    label="No. Interior"
                    value={formik.values.noInterior}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.noInterior && Boolean(formik.errors.noInterior)}
                    helperText={formik.touched.noInterior ? formik.errors.noInterior : ""}
                    margin="normal"
                    disabled={isLoading || formik.isSubmitting}
                    inputProps={{ maxLength: 50 }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <FormControl
              fullWidth
              required
              sx={{
                ...customStylesAsterisk,
                mt: 1,
              }}
              error={formik.touched.coloniaId && Boolean(formik.errors.coloniaId)}
            >
              <InputLabel id={"coloniaId"}>Colonia</InputLabel>
              <Select
                labelId="coloniaId"
                name="coloniaId"
                id="coloniaId"
                fullWidth
                required
                label="Colonia"
                value={formik.values.coloniaId}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.coloniaId && Boolean(formik.errors.coloniaId)}
                sx={{ textAlign: "left" }}
                disabled={isLoading || formik.isSubmitting}
              >
                <MenuItem value="">Selecciona</MenuItem>
                {colonias.map((colonia) => (
                  <MenuItem key={colonia.id} value={colonia.id}>
                    {colonia.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.coloniaId && formik.errors.coloniaId && (
                <FormHelperText>{formik.errors.coloniaId}</FormHelperText>
              )}
            </FormControl>

            {String(formik.values.coloniaId) === "-1" && (
              <>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} md={12}>
                    <TextField
                      id="otro"
                      name="otro"
                      label="Otra Colonia"
                      margin="normal"
                      required
                      fullWidth
                      autoComplete="off"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={formik.touched.otro && Boolean(formik.errors.otro)}
                      helperText={formik.touched.otro && formik.errors.otro}
                      value={formik.values.otro}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <FormControl fullWidth>
              <TextField
                fullWidth
                id="ciudad"
                name="ciudad"
                label="Ciudad"
                value={formik.values.ciudad}
                error={formik.touched.ciudadId && Boolean(formik.errors.ciudadId)}
                helperText={formik.touched.ciudadId ? formik.errors.ciudadId : ""}
                margin="normal"
                disabled={true}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                fullWidth
                id="localidadId"
                name="localidadId"
                label="Municipio"
                value={formik.values.localidad}
                error={formik.touched.localidadId && Boolean(formik.errors.localidadId)}
                helperText={formik.touched.localidadId ? formik.errors.localidadId : ""}
                margin="normal"
                disabled={true}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                fullWidth
                id="estado"
                name="estado"
                label="Entidad federativa"
                value={formik.values.estado}
                error={formik.touched.estadoId && Boolean(formik.errors.estadoId)}
                helperText={formik.touched.estadoId ? formik.errors.estadoId : ""}
                margin="normal"
                disabled={true}
              />
            </FormControl>

            <Grid container columnSpacing={1} my={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="blue_outlined"
                  sx={{ padding: "0", height: "100%" }}
                  onClick={handleOnClickRestart}
                  disabled={formik.isSubmitting}
                >
                  Regresar al inicio
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  color="primary"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionConyugueAval;
